import type { Feature } from '@/content/features';
import FeatureIcon from '@/components/ui/FeatureIcon';

interface FeatureCardProps {
  feature: Feature | { title: string; description: string; icon: string };
}

/**
 * FeatureCard Component
 * Displays a single feature with SVG icon, title, and description
 * Supports both emoji icons (for Features page) and SVG icons (for Home page)
 */

export default function FeatureCard({ feature }: FeatureCardProps) {
  // Check if icon is an emoji or SVG icon name
  const isEmoji = feature.icon.length <= 2;

  return (
    <div className="bg-white dark:bg-secondary-800 rounded-2xl p-6 border border-secondary-200 dark:border-secondary-700 shadow-soft hover:shadow-soft-lg transition-all duration-300 group">
      {/* Icon */}
      <div className="w-14 h-14 bg-gradient-to-br from-primary-100 to-primary-50 dark:from-primary-900 dark:to-primary-950 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
        {isEmoji ? (
          <span className="text-3xl" role="img" aria-hidden="true">
            {feature.icon}
          </span>
        ) : (
          <div className="text-primary-600 dark:text-primary-200">
            <FeatureIcon name={feature.icon} className="w-8 h-8" />
          </div>
        )}
      </div>

      {/* Title */}
      <h3 className="text-xl font-semibold text-secondary-900 dark:text-secondary-50 mb-3">
        {feature.title}
      </h3>

      {/* Description */}
      <p className="text-secondary-700 dark:text-secondary-300 leading-relaxed">
        {feature.description}
      </p>
    </div>
  );
}

